// Thuế tính theo từng món: mỗi món có thuế suất riêng, thuế của đơn là tổng thuế
// các món. Đơn tạo trước khi tách thuế theo món chỉ có thuế ở cấp đơn, nên mọi
// hàm ở đây đều nhận thuế suất của cả đơn làm mức dự phòng để số tiền của đơn cũ
// hiển thị không đổi.
export interface TaxItem {
  price?: number;
  quantity?: number;
  taxRate?: number | null;
  tax?: number | null;
}

export interface TaxOrder {
  items?: TaxItem[];
  subtotal?: number;
  taxRate?: number | null;
  tax?: number | null;
  discount?: number;
}

const num = (v: any) => Number(v) || 0;
const isSet = (v: any) => v !== undefined && v !== null;

// Thuế suất (%) áp cho một dòng món.
export function itemTaxRate(item: TaxItem, fallbackRate = 0): number {
  return isSet(item?.taxRate) ? num(item.taxRate) : num(fallbackRate);
}

// Tiền thuế của một dòng món.
export function itemTax(item: TaxItem, fallbackRate = 0): number {
  if (isSet(item?.tax)) return num(item.tax);
  return num(item?.price) * num(item?.quantity) * (itemTaxRate(item, fallbackRate) / 100);
}

// Thành tiền của một dòng món (đã gồm thuế).
export function itemTotal(item: TaxItem, fallbackRate = 0): number {
  return num(item?.price) * num(item?.quantity) + itemTax(item, fallbackRate);
}

export function orderSubtotal(order: TaxOrder): number {
  if (isSet(order?.subtotal)) return num(order.subtotal);
  return (order?.items || []).reduce((s, i) => s + num(i.price) * num(i.quantity), 0);
}

// Tổng thuế của đơn = tổng thuế các món.
export function orderTax(order: TaxOrder): number {
  const items = order?.items || [];
  if (items.length > 0) {
    const fallbackRate = num(order?.taxRate);
    return items.reduce((s, i) => s + itemTax(i, fallbackRate), 0);
  }
  if (isSet(order?.tax)) return num(order.tax);
  return orderSubtotal(order) * (num(order?.taxRate) / 100);
}

export function orderTotal(order: TaxOrder): number {
  return orderSubtotal(order) + orderTax(order) - num(order?.discount);
}

const trim = (n: number) => Number(n.toFixed(2)).toLocaleString('vi-VN');

// Nhãn thuế suất: "10%" khi mọi món cùng một mức, "TB 8,5%" (trung bình) khi các
// món chịu thuế khác nhau.
export function taxRateLabel(order: TaxOrder): string {
  const items = order?.items || [];
  const fallbackRate = num(order?.taxRate);
  const rates = Array.from(new Set(items.map((i) => itemTaxRate(i, fallbackRate))));
  if (rates.length <= 1) return `${trim(rates.length === 1 ? rates[0] : fallbackRate)}%`;
  const subtotal = orderSubtotal(order);
  return subtotal > 0 ? `TB ${trim((orderTax(order) / subtotal) * 100)}%` : '0%';
}

// Một phiên đặt nhóm gồm nhiều đơn con; thuế của phiên = tổng thuế các đơn con.
export function groupSubtotal(group: { orders?: TaxOrder[] }): number {
  return (group?.orders || []).reduce((s, o) => s + orderSubtotal(o), 0);
}

export function groupTax(group: { orders?: TaxOrder[] }): number {
  return (group?.orders || []).reduce((s, o) => s + orderTax(o), 0);
}

export function groupTotal(group: { orders?: TaxOrder[] }): number {
  return groupSubtotal(group) + groupTax(group);
}
